<template>
  <div class="import-failure-prompt" data-test="failure-prompt" @keydown.esc.stop="emit('answer', false)">
    <div
      ref="card"
      class="import-failure-prompt__card"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="import-failure-prompt-title"
      aria-describedby="import-failure-prompt-message"
      tabindex="-1"
    >
      <h3 id="import-failure-prompt-title" class="import-failure-prompt__title">{{ copy.title }}</h3>
      <p id="import-failure-prompt-message" class="import-failure-prompt__message">{{ copy.message }}</p>
      <ul v-if="copy.names.length" class="import-failure-prompt__names" data-test="failure-prompt-names">
        <li v-for="name in copy.names" :key="name">{{ name }}</li>
        <li v-if="copy.more" aria-hidden="true">…</li>
      </ul>
      <div class="import-failure-prompt__actions">
        <button type="button" class="import-failure-prompt__dont" data-test="failure-prompt-dont-send" @click="emit('answer', false)">
          {{ $t('import_files.failure_report.dont_send') }}
        </button>
        <button type="button" class="import-failure-prompt__send" data-test="failure-prompt-send" @click="emit('answer', true)">
          {{ $t('import_files.failure_report.send') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import type { ImportFailurePromptCopy } from '../../utils/import/importFailureReport';

// Asks whether to send the files Import files couldn't read or save (importFailureReport.ts).
// Sits over the dialog, so nothing behind it can be used until it's answered; Esc is "Don't send".
defineProps<{ copy: ImportFailurePromptCopy }>();
const emit = defineEmits<{ (e: 'answer', send: boolean): void }>();

const card = ref<HTMLElement | null>(null);

onMounted(async () => {
  await nextTick();
  card.value?.focus();
});
</script>

<style scoped>
.import-failure-prompt {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(9, 10, 13, 0.62);
}
.import-failure-prompt__card {
  width: min(420px, 100%);
  max-height: 100%;
  overflow-y: auto;
  padding: 20px;
  border-radius: 14px;
  background: var(--dark);
  color: var(--white);
  box-shadow: 0 20px 60px -16px rgba(0, 0, 0, 0.9);
  outline: none;
}
.import-failure-prompt__title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 800;
}
.import-failure-prompt__message {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.import-failure-prompt__names {
  margin: 10px 0 0;
  padding-left: 18px;
  font-size: 13px;
  opacity: 0.8;
  overflow-wrap: anywhere;
}
.import-failure-prompt__actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 18px;
}
.import-failure-prompt__dont,
.import-failure-prompt__send {
  height: 40px;
  border: 0;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}
.import-failure-prompt__dont {
  padding: 0 4px;
  background: none;
  color: var(--white);
}
.import-failure-prompt__send {
  padding: 0 24px;
  border-radius: 999px;
  background: var(--yellow);
  color: #000;
  font-weight: 800;
}
</style>
